import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, TrendingUp, DollarSign, MessageSquare, BarChart3 } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

interface IntentCluster {
  cluster_id: number;
  label: string;
  question_count: number;
  total_search_volume: number;
  avg_cpc: number;
  total_opportunity_value: number;
  sample_questions: string[];
  answer_types: Record<string, number>;
  sources: Record<string, number>;
  centroid: { x: number; y: number };
  questions: any[];
}

interface ClusterAnalysis {
  wheel: {
    id: string;
    center_term: string;
    total_questions: number;
    total_search_volume: number;
    total_opportunity_value: number;
  };
  intent_clusters: IntentCluster[];
  visualization: {
    clusters: any[];
  };
}

interface QuestionWheel {
  id: string;
  center_term: string;
  total_questions: number;
  created_at: string;
}

export default function CategoryIntentClusters() {
  const { selectedSite } = useSite();
  const [wheels, setWheels] = useState<QuestionWheel[]>([]);
  const [selectedWheel, setSelectedWheel] = useState<string>('');
  const [analysis, setAnalysis] = useState<ClusterAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<IntentCluster | null>(null);

  // Fetch available wheels on mount
  useEffect(() => {
    if (selectedSite?.id) {
      fetchWheels();
    }
  }, [selectedSite?.id]);

  const fetchWheels = async () => {
    if (!selectedSite?.id) return;

    try {
      setLoading(true);
      const response = await authFetch(
        `/api/sites/${selectedSite.id}/question-wheels`
      );
      const data = await response.json();
      setWheels(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching wheels:', error);
      setWheels([]);
    } finally {
      setLoading(false);
    }
  };

  const runClusterAnalysis = async () => {
    if (!selectedWheel) return;

    try {
      setAnalyzing(true);
      const response = await authFetch(
        `/api/question-wheels/${selectedWheel}/cluster-analysis`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        setAnalysis(data);
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('Error running cluster analysis:', error);
      alert('Failed to run cluster analysis: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setAnalyzing(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Category Intent Clusters</h1>
        <p className="text-muted-foreground mt-2">
          Discover and visualize customer intent patterns from question research
        </p>
      </div>

      {/* Wheel Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Question Wheel</CardTitle>
          <CardDescription>
            Choose a question wheel to analyze customer intent clusters
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Question Wheel</label>
              <Select value={selectedWheel} onValueChange={setSelectedWheel}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a question wheel..." />
                </SelectTrigger>
                <SelectContent>
                  {wheels.map((wheel) => (
                    <SelectItem key={wheel.id} value={wheel.id}>
                      {wheel.center_term} ({wheel.total_questions} questions)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={runClusterAnalysis}
              disabled={!selectedWheel || analyzing}
            >
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                'Analyze Intent Clusters'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysis && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Category</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analysis.wheel.center_term}</div>
                <p className="text-xs text-muted-foreground">
                  {formatNumber(analysis.wheel.total_questions)} total questions
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Intent Clusters</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analysis.intent_clusters.length}</div>
                <p className="text-xs text-muted-foreground">
                  Distinct customer intent groups
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Search Volume</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatNumber(analysis.wheel.total_search_volume)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Monthly searches
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Opportunity Value</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(analysis.wheel.total_opportunity_value)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Total monthly opportunity
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Top Clusters */}
          <Card>
            <CardHeader>
              <CardTitle>Top Intent Clusters</CardTitle>
              <CardDescription>
                Ranked by opportunity value (search volume × CPC)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {analysis.intent_clusters.slice(0, 6).map((cluster, index) => (
                  <Card
                    key={cluster.cluster_id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedCluster(cluster)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <Badge className="mb-2">#{index + 1}</Badge>
                          <h3 className="font-semibold text-sm">{cluster.label}</h3>
                        </div>
                        <Badge variant="outline">{cluster.question_count}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Search Volume</span>
                        <span className="font-medium">
                          {formatNumber(cluster.total_search_volume)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Opportunity</span>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(cluster.total_opportunity_value)}
                        </span>
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground italic line-clamp-2">
                          {cluster.sample_questions[0]}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Intent Clusters</CardTitle>
              <CardDescription>
                Complete breakdown of customer intent patterns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Intent Cluster</TableHead>
                    <TableHead className="text-right">Questions</TableHead>
                    <TableHead className="text-right">Search Volume</TableHead>
                    <TableHead className="text-right">Avg CPC</TableHead>
                    <TableHead className="text-right">Opportunity Value</TableHead>
                    <TableHead>Answer Types</TableHead>
                    <TableHead>Sources</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analysis.intent_clusters.map((cluster, index) => (
                    <TableRow
                      key={cluster.cluster_id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedCluster(cluster)}
                    >
                      <TableCell>
                        <Badge variant="outline">#{index + 1}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-semibold">{cluster.label}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                            {cluster.sample_questions[0]}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{cluster.question_count}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(cluster.total_search_volume)}
                      </TableCell>
                      <TableCell className="text-right">
                        ${cluster.avg_cpc.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        {formatCurrency(cluster.total_opportunity_value)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(cluster.answer_types)
                            .slice(0, 2)
                            .map(([type, count]) => (
                              <Badge key={type} variant="secondary" className="text-xs">
                                {type}: {count}
                              </Badge>
                            ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(cluster.sources)
                            .slice(0, 2)
                            .map(([source, count]) => (
                              <Badge key={source} variant="outline" className="text-xs">
                                {source}: {count}
                              </Badge>
                            ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Cluster Details Modal */}
          {selectedCluster && (
            <div
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedCluster(null)}
            >
              <Card
                className="max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{selectedCluster.label}</CardTitle>
                      <CardDescription>
                        {selectedCluster.question_count} questions • {formatCurrency(selectedCluster.total_opportunity_value)} opportunity
                      </CardDescription>
                    </div>
                    <Button variant="ghost" onClick={() => setSelectedCluster(null)}>
                      Close
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Search Volume</p>
                      <p className="text-2xl font-bold">
                        {formatNumber(selectedCluster.total_search_volume)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Average CPC</p>
                      <p className="text-2xl font-bold">
                        ${selectedCluster.avg_cpc.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Opportunity Value</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(selectedCluster.total_opportunity_value)}
                      </p>
                    </div>
                  </div>

                  {/* Sample Questions */}
                  <div>
                    <h3 className="font-semibold mb-3">Sample Questions</h3>
                    <ul className="space-y-2">
                      {selectedCluster.sample_questions.map((q, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Badge variant="outline" className="mt-0.5">{idx + 1}</Badge>
                          <span className="text-sm">{q}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Answer Types Distribution */}
                  <div>
                    <h3 className="font-semibold mb-3">Answer Types</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(selectedCluster.answer_types).map(([type, count]) => (
                        <Badge key={type} variant="secondary">
                          {type}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Sources */}
                  <div>
                    <h3 className="font-semibold mb-3">Data Sources</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(selectedCluster.sources).map(([source, count]) => (
                        <Badge key={source} variant="outline">
                          {source}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
